import React, { useState } from "react";
import type { WalletInfo } from "../../../services/walletService";
import SubtrackWallet from "../../../assets/images/subtrackWallet.svg";
import TalismanWallet from "../../../assets/images/talisman.svg";
import PolkadotWallet from "../../../assets/images/polkadotWallet.svg";
import WalletAddress from "../../../assets/images/WalletAddress.svg";
import Copy from "../../../assets/images/copy.svg";
import Balance from "../../../assets/images/balance.svg";
import Logout from "../../../assets/images/logout.svg";
import AngleDown from "../../../assets/images/angleDown.svg";
import AngleUp from "../../../assets/images/angleUp.svg";
import { Button } from "../Button";
import { useBalance, useWallet } from "@/contexts";
import { convertToAnalogAddress, formatAmount } from "@/utils";

interface ConnectedWalletDropdownProps {
  walletInfo: WalletInfo;
  onDisconnect: () => void;
}

export const getWalletIcon = (provider: string) => {
  switch (provider.toLowerCase()) {
    case "subwallet-js":
      return SubtrackWallet;
    case "talisman":
      return TalismanWallet;
    case "polkadot-js":
      return PolkadotWallet;
    default:
      return SubtrackWallet; // fallback icon
  }
};

export const ConnectedWalletDropdown: React.FC<
  ConnectedWalletDropdownProps
> = ({ walletInfo, onDisconnect }) => {
  const baseClasses = "relative";
  const cornerBorderClasses = `
  after:h-1 after:w-1 after:top-0 after:left-0 after:border-t after:border-l after:border-accentPurple after:absolute 
  before:h-1 before:w-1 before:top-0 before:right-0 before:border-t before:border-r before:border-accentPurple before:absolute
  `;

  const innerCornerBorderClasses = `
after:h-1 after:w-1 after:bottom-0 after:left-0 after:border-b after:border-l after:border-accentPurple after:absolute 
before:h-1 before:w-1 before:bottom-0 before:right-0 before:border-b before:border-r before:border-accentPurple before:absolute
`;

  const classes = `${baseClasses} ${cornerBorderClasses} }`;
  const innerClasses = `cursor-pointer p-5 bg-modalBg border bg-bgCard border-cardBorder text-white  ${innerCornerBorderClasses}`;

  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { totalBalance } = useBalance();
  const { selectedAccount } = useWallet();

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(walletInfo.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy address:", error);
    }
  };

  const truncateAddress = (address: string) => {
    if (address.length <= 12) return address;
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  };

  const getWalletName = (provider: string) => {
    switch (provider.toLowerCase()) {
      case "subwallet-js":
        return "Subwallet";
      case "talisman":
        return "Talisman";
      case "polkadot-js":
        return "Polkadot.js";
      default:
        return "Connected Wallet";
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 border border-accentPurple bg-dropdownBg rounded-lg hover:bg-darkBorder transition-colors"
      >
        <div className="w-6 h-6 flex items-center justify-center text-sm">
          <img src={getWalletIcon(walletInfo.provider)} alt="Wallet Icon" />
        </div>
        <span className="text-white text-sm font-medium uppercase tracking-wide">
          {getWalletName(walletInfo.provider)}
        </span>
        {isOpen ? (
          <img
            src={AngleUp}
            alt="Dropdown Arrow Up"
            className="w-4 h-4 text-white"
          />
        ) : (
          <img
            src={AngleDown}
            alt="Dropdown Arrow Down"
            className="w-4 h-4 text-white"
          />
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 shadow-lg z-50">
          <div className={classes}>
            <div className={innerClasses}>
              <div className="flex justify-between w-full items-center gap-2 ">
                <span className="text-sm text-white">Wallet Address</span>
                <img src={WalletAddress} alt="Wallet Address" />
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-white/60 font-medium">
                  {selectedAccount &&
                    truncateAddress(
                      convertToAnalogAddress(selectedAccount.address)
                    )}
                </span>
                <Button
                  onClick={handleCopyAddress}
                  className="bg-transparent !p-0"
                >
                  {copied ? (
                    <span className="text-xs text-green-400 font-medium">
                      Address Copied!
                    </span>
                  ) : (
                    <img src={Copy} alt="Copy" />
                  )}
                </Button>
              </div>
            </div>
          </div>
          <div className={classes}>
            <div className={innerClasses}>
              <div className="flex flex-col gap-2">
                <div className="flex items-center w-full justify-between gap-2">
                  <span className="text-sm text-white">Balance</span>
                  <img src={Balance} alt="Balance" />
                </div>
                <span className="text-sm text-white/60 font-medium">
                  ≈ {formatAmount(totalBalance)}
                </span>
              </div>
            </div>
          </div>
          <div className={classes}>
            <div className={innerClasses}>
              <Button
                onClick={onDisconnect}
                variant="link"
                className="bg-transparent text-errorText capitalize justify-between flex items-center w-full !p-0"
              >
                <span className="text-sm font-medium">Disconnect Wallet</span>
                <img src={Logout} alt="Logout" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
      )}
    </div>
  );
};
